import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiErr, API_URL } from "@/lib/api";
import { Coins, Users, Video, Heart, Camera, Edit3, Trash2, Rocket, Star, X } from "lucide-react";
import VideoCard from "@/components/VideoCard";
import { PushToggle } from "@/components/PushToggle";
import { toast } from "sonner";

export default function Profile() {
  const { uid } = useParams();
  const { user, ready, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const targetId = uid || user?.id;
  const [editing, setEditing] = useState(false);
  const [videoEditing, setVideoEditing] = useState(null);

  const load = () => { if (targetId) api.get(`/users/${targetId}`).then(r=>setP(r.data)).catch(e=>toast.error(apiErr(e))); };
  useEffect(load, [targetId]);

  if (ready && !user && !uid) { nav("/login"); return null; }
  if (!p) return <div className="p-8 text-white/60">Loading…</div>;

  const isSelf = user && user.id === p.id;

  const uploadImmediate = async (file, kind) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      fd.append("kind", kind === "cover" ? "thumbnail" : "avatar");
      const { data } = await api.post("/upload/file", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const url = API_URL.replace(/\/api$/, "") + data.url;
      const patch = kind === "cover" ? { cover_url: url } : { avatar_url: url };
      await api.put("/me", patch);
      toast.success(kind === "cover" ? "Cover updated" : "Profile photo updated");
      load(); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/videos/${id}`); toast.success("Deleted"); load(); }
    catch(e){toast.error(apiErr(e));}
  };
  const boost = async (id) => {
    try { const s = (await api.get(`/videos/${id}/promo-status`)).data;
      if (!window.confirm(`Boost this video for ${s.boost_cost} coins (${s.boost_days} days)?`)) return;
      await api.post(`/videos/${id}/boost`); toast.success("Boosted!"); refresh(); load(); }
    catch(e){toast.error(apiErr(e));}
  };
  const featurePay = async (id) => {
    try { const s = (await api.get(`/videos/${id}/promo-status`)).data;
      if (!window.confirm(`Feature this video for ${s.feature_cost} coins (${s.feature_days} days)?`)) return;
      await api.post(`/videos/${id}/pay-feature`); toast.success("Featured!"); refresh(); load(); }
    catch(e){toast.error(apiErr(e));}
  };

  return (
    <div>
      {/* Cover */}
      <div className="relative w-full h-40 sm:h-56 bg-gradient-to-br from-[#08D9D6]/30 via-[#0a0a0e] to-[#FF2E63]/20 overflow-hidden">
        {p.cover_url && <img src={p.cover_url} alt="cover" className="absolute inset-0 w-full h-full object-cover"/>}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"/>
        {isSelf && (
          <label className="absolute top-3 right-3 z-10 h-9 px-3 rounded-full bg-black/70 backdrop-blur text-xs font-semibold flex items-center gap-1 cursor-pointer jx-btn hover:bg-black/90" data-testid="cover-upload-label">
            <Camera size={12}/> Change cover
            <input type="file" accept="image/*" hidden data-testid="cover-upload-input"
                   onChange={e=>uploadImmediate(e.target.files?.[0], "cover")}/>
          </label>
        )}
      </div>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 -mt-12 relative">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#08D9D6] to-[#FF2E63] flex items-center justify-center text-black text-4xl font-extrabold border-4 border-[#050505] overflow-hidden shrink-0">
            {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover"/> : p.username[0].toUpperCase()}
            {isSelf && (
              <>
                <label className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center cursor-pointer" data-testid="avatar-upload-label">
                  <Camera size={20} className="text-white"/>
                  <input type="file" accept="image/*" hidden data-testid="avatar-upload-input"
                         onChange={e=>uploadImmediate(e.target.files?.[0], "avatar")}/>
                </label>
                <label className="sm:hidden absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#08D9D6] border-2 border-[#050505] flex items-center justify-center cursor-pointer jx-btn shadow-lg">
                  <Camera size={14} className="text-black"/>
                  <input type="file" accept="image/*" hidden data-testid="avatar-upload-mobile"
                         onChange={e=>uploadImmediate(e.target.files?.[0], "avatar")}/>
                </label>
              </>
            )}
          </div>
          <div className="flex-1">
            <h1 className="h-font text-3xl font-extrabold">@{p.username}</h1>
            <p className="text-white/70 mt-1">{p.bio || "No bio yet."}</p>
            <div className="flex flex-wrap gap-4 mt-3 text-sm">
              <Stat icon={Users} label="Followers" val={p.followers}/>
              <Stat icon={Video} label="Videos" val={p.videos.length}/>
              <Stat icon={Heart} label="Total likes" val={p.total_likes}/>
              {isSelf && user.role==="admin" && <Stat icon={Coins} label="Coins" val={Math.floor(p.coins)} accent="#FFD700"/>}
            </div>
          </div>
          {isSelf && (
            <button onClick={()=>setEditing(true)} data-testid="edit-profile-btn"
                    className="jx-btn h-10 px-4 rounded-full bg-[#08D9D6] text-black font-bold text-sm inline-flex items-center gap-2">
              <Edit3 size={14}/> Edit profile
            </button>
          )}
        </div>
        {isSelf && (
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/coins" className="jx-btn h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 font-semibold text-sm">Wallet</Link>
            <Link to="/upload" className="jx-btn h-9 px-4 rounded-full border border-white/15 hover:bg-white/5 font-semibold text-sm">Upload</Link>
            <button onClick={()=>{logout(); nav('/');}} data-testid="profile-logout" className="jx-btn h-9 px-4 rounded-full border border-white/15 hover:bg-white/5 font-semibold text-sm">Sign out</button>
            <PushToggle testid="profile-push-toggle"/>
            <Link to="/downloads" data-testid="profile-downloads-link" className="jx-btn h-9 px-4 rounded-full border border-white/15 hover:bg-white/5 font-semibold text-sm inline-flex items-center">Downloads</Link>
          </div>
        )}

        <h2 className="h-font text-xl font-bold mt-10">Uploads</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-8 mt-4">
          {p.videos.map(v => (
            <div key={v.id} className="relative">
              <VideoCard v={v}/>
              {isSelf && (
                <div className="mt-2 flex flex-wrap gap-1">
                  <button onClick={()=>setVideoEditing(v)} data-testid={`edit-video-${v.id}`}
                          className="jx-btn h-8 px-2 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold inline-flex items-center gap-1"><Edit3 size={12}/> Edit</button>
                  <button onClick={()=>delVideo(v.id)} data-testid={`del-video-${v.id}`}
                          className="jx-btn h-8 px-2 rounded-full bg-[#FF2E63]/20 text-[#FF2E63] text-xs font-semibold inline-flex items-center gap-1"><Trash2 size={12}/> Delete</button>
                  <button onClick={()=>boost(v.id)} data-testid={`boost-video-${v.id}`}
                          className="jx-btn h-8 px-2 rounded-full bg-[#08D9D6]/20 text-[#08D9D6] text-xs font-semibold inline-flex items-center gap-1"><Rocket size={12}/> Boost</button>
                  <button onClick={()=>featurePay(v.id)} data-testid={`feat-video-${v.id}`}
                          className="jx-btn h-8 px-2 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-xs font-semibold inline-flex items-center gap-1"><Star size={12}/> Feature</button>
                </div>
              )}
            </div>
          ))}
          {p.videos.length===0 && <div className="text-white/50 text-sm">No videos yet.</div>}
        </div>
      </div>

      {editing && <EditProfile onClose={()=>{setEditing(false); load(); refresh();}} user={p}/>}
      {videoEditing && <EditVideo v={videoEditing} onClose={(ok)=>{setVideoEditing(null); if (ok) load();}}/>}
    </div>
  );
}

function Stat({ icon: Icon, label, val, accent }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} style={accent ? {color: accent} : {}} className="text-white/60"/>
      <span className="font-bold" style={accent ? {color: accent} : {}}>{val?.toLocaleString?.() ?? val}</span>
      <span className="text-white/50">{label}</span>
    </div>
  );
}

function EditProfile({ user, onClose }) {
  const [bio, setBio] = useState(user.bio || "");
  const [username, setUsername] = useState(user.username);
  const [avatar, setAvatar] = useState(user.avatar_url || "");
  const [cover, setCover] = useState(user.cover_url || "");
  const [busy, setBusy] = useState(false);

  const uploadFile = async (file, kind) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    const { data } = await api.post("/upload/file", fd, { headers: { "Content-Type": "multipart/form-data" }});
    return API_URL.replace(/\/api$/, "") + data.url;
  };
  const onFile = async (f, kind) => {
    if (!f) return;
    try { setBusy(true); const url = await uploadFile(f, kind === "cover" ? "thumbnail" : "avatar");
      if (kind === "cover") setCover(url); else setAvatar(url); toast.success("Uploaded"); }
    catch(e){toast.error(apiErr(e));} finally { setBusy(false); }
  };
  const save = async () => {
    try { setBusy(true);
      await api.put("/me", { bio, username, avatar_url: avatar, cover_url: cover });
      toast.success("Profile saved"); onClose(); }
    catch(e){toast.error(apiErr(e));} finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="bg-[#121216] border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
        <div className="flex items-center justify-between">
          <h3 className="h-font text-xl font-bold">Edit profile</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16}/></button>
        </div>
        <div className="mt-4 space-y-4">
          <div>
            <div className="relative w-full h-32 rounded-xl overflow-hidden bg-white/5">
              {cover ? <img src={cover} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full bg-gradient-to-br from-[#08D9D6]/30 to-[#FF2E63]/30"/>}
              <label className="absolute bottom-2 right-2 h-9 px-3 rounded-full bg-black/70 text-xs font-semibold flex items-center gap-1 cursor-pointer">
                <Camera size={12}/> Cover
                <input type="file" accept="image/*" hidden onChange={e=>onFile(e.target.files?.[0], "cover")} data-testid="upload-cover"/>
              </label>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-[#08D9D6] to-[#FF2E63] flex items-center justify-center text-black text-2xl font-extrabold shrink-0">
              {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover"/> : username[0].toUpperCase()}
              <label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 cursor-pointer">
                <Camera size={16} className="text-white"/>
                <input type="file" accept="image/*" hidden onChange={e=>onFile(e.target.files?.[0], "avatar")} data-testid="upload-avatar"/>
              </label>
            </div>
            <div className="flex-1">
              <label className="text-xs text-white/60">Username</label>
              <input value={username} onChange={e=>setUsername(e.target.value)} data-testid="profile-username"
                     className="mt-1 w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3"/>
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60">Bio</label>
            <textarea value={bio} onChange={e=>setBio(e.target.value)} rows={3} data-testid="profile-bio"
                      className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2"/>
          </div>
          <button onClick={save} disabled={busy} data-testid="save-profile-btn"
                  className="jx-btn w-full h-11 rounded-xl bg-[#08D9D6] text-black font-bold hover:bg-[#06B2B0] disabled:opacity-50">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditVideo({ v, onClose }) {
  const [title, setTitle] = useState(v.title);
  const [description, setDescription] = useState(v.description);
  const [hashtags, setHashtags] = useState((v.hashtags||[]).map(h=>`#${h}`).join(" "));
  const [category, setCategory] = useState(v.category);
  const [visibility, setVisibility] = useState(v.visibility);
  const [isPaid, setIsPaid] = useState(!!v.is_paid);
  const [price, setPrice] = useState(v.price_coins || 250);
  const [allowComments, setAllowComments] = useState(v.allow_comments !== false);
  const [allowSharing, setAllowSharing] = useState(v.allow_sharing !== false);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);

  useEffect(()=>{ api.get("/categories").then(r=>setCats(r.data.filter(c=>c!=="All"))); }, []);

  const save = async () => {
    try { setBusy(true);
      await api.put(`/videos/${v.id}`, {
        title, description,
        hashtags: hashtags.split(/[,\s]+/).map(h=>h.replace(/^#/,"").trim()).filter(Boolean),
        category, visibility, is_paid: isPaid, price_coins: Number(price),
        allow_comments: allowComments, allow_sharing: allowSharing,
      });
      toast.success("Video updated"); onClose(true); }
    catch(e){toast.error(apiErr(e));} finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={()=>onClose(false)}>
      <div onClick={e=>e.stopPropagation()} className="bg-[#121216] border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-video-dialog">
        <div className="flex items-center justify-between">
          <h3 className="h-font text-xl font-bold">Edit video</h3>
          <button onClick={()=>onClose(false)} className="p-2 rounded-full hover:bg-white/5"><X size={16}/></button>
        </div>
        <div className="mt-4 space-y-3">
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title" data-testid="edit-title"
                 className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3"/>
          <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={3} placeholder="Description" data-testid="edit-description"
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2"/>
          <input value={hashtags} onChange={e=>setHashtags(e.target.value)} placeholder="#hashtags (space separated)" data-testid="edit-hashtags"
                 className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={category} onChange={e=>setCategory(e.target.value)} data-testid="edit-category"
                    className="h-11 rounded-xl bg-white/5 border border-white/10 px-3">
              {cats.map(c=><option key={c} value={c} className="bg-black">{c}</option>)}
            </select>
            <select value={visibility} onChange={e=>setVisibility(e.target.value)} data-testid="edit-visibility"
                    className="h-11 rounded-xl bg-white/5 border border-white/10 px-3">
              <option className="bg-black" value="public">Public</option>
              <option className="bg-black" value="unlisted">Unlisted</option>
              <option className="bg-black" value="private">Private</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isPaid} onChange={e=>setIsPaid(e.target.checked)} className="w-4 h-4 accent-[#FFD700]" data-testid="edit-paid"/>
            <span className="text-[#FFD700] font-semibold">Paid video</span>
          </label>
          {isPaid && (
            <input type="number" value={price} onChange={e=>setPrice(e.target.value)} placeholder="Price in coins" data-testid="edit-price"
                   className="w-full h-11 rounded-xl bg-white/5 border border-[#FFD700]/40 px-3 text-[#FFD700] font-bold"/>
          )}
          <label className="flex items-center justify-between text-sm cursor-pointer"><span>Allow comments</span>
            <input type="checkbox" checked={allowComments} onChange={e=>setAllowComments(e.target.checked)} className="w-4 h-4 accent-[#08D9D6]"/>
          </label>
          <label className="flex items-center justify-between text-sm cursor-pointer"><span>Allow sharing</span>
            <input type="checkbox" checked={allowSharing} onChange={e=>setAllowSharing(e.target.checked)} className="w-4 h-4 accent-[#08D9D6]"/>
          </label>
          <button onClick={save} disabled={busy} data-testid="save-video-btn"
                  className="jx-btn w-full h-11 rounded-xl bg-[#08D9D6] text-black font-bold hover:bg-[#06B2B0] disabled:opacity-50">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
